import { createContext, memo, useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { profileApi } from '@internal/api/profile';
import Loader from '@internal/components/common/loader';
import Routes from '@internal/config/routes';
import { ApplicationStage, Profile } from '@internal/types/profile';
import { isPhase1Stage } from '@internal/utils/application-stage';

interface BaseApiContextValue {
  token: string;
  profile?: Profile | null;
  profileIsLoading: boolean;
  isCandidate: boolean;
  isPartnerCandidate: boolean;
  isPartnerDeveloper: boolean;
  isPartnerManager: boolean;
  isDeveloper: boolean;
  isCandidateOrPartnerCandidate: boolean;
}

interface StrictApiContextValue extends BaseApiContextValue {
  profile: Profile;
  token: string;
}

const ApiContext = createContext<BaseApiContextValue>(
  {} as BaseApiContextValue
);

const PHASE1_ROUTES: ReadonlySet<string> = new Set([
  Routes.Roles,
  Routes.GetSetUp,
  Routes.Profile,
  `${Routes.Profile}/[[...section]]`,
  Routes.OpenProjects,
  Routes.MyApplications,
  `${Routes.MyApplications}/[dealId]`,
  Routes.Settings,
  Routes.Faq,
  `${Routes.ViewProject}/[dealId]`,
]);

interface ApiContextProviderProps {
  token?: string;
  notLogginedAllowed?: boolean;
  children: React.ReactNode;
}

export const ApiContextProvider = memo(
  ({
    token,
    children,
    notLogginedAllowed = false,
  }: ApiContextProviderProps) => {
    const router = useRouter();

    const { profile, profileIsLoading } = profileApi.getProfile.useQuery(null, {
      enabled: Boolean(token),
      staleTime: Infinity,
    });

    const [isProfileClarified, setIsProfileClarified] = useState(false);

    useEffect(() => {
      if (!profile) return;

      const isPhase1 = isPhase1Stage(profile.applicationStage);
      const onPhase1Route = PHASE1_ROUTES.has(router.pathname);
      const isTerminalPhase1 =
        profile.applicationStage === ApplicationStage.TechPreScreeningCompleted;

      if (!isPhase1) {
        setIsProfileClarified(true);
        return;
      }

      if (notLogginedAllowed) return;

      if (!onPhase1Route && !isTerminalPhase1) {
        router.replace(Routes.GetSetUp);
        return;
      }

      setIsProfileClarified(true);
    }, [profile]);

    return (
      <ApiContext.Provider
        value={{
          profile,
          profileIsLoading,
          isPartnerManager: profile?.role === 'partner manager',
          isCandidate: profile?.role === 'candidate',
          isDeveloper: profile?.role === 'developer',
          isPartnerDeveloper: profile?.role === 'partner developer',
          isPartnerCandidate: profile?.role === 'partner candidate',
          isCandidateOrPartnerCandidate:
            profile?.role === 'candidate' ||
            profile?.role === 'partner candidate',
          // Temporaly force it to be a string
          token: token!,
        }}
      >
        {notLogginedAllowed || (profile && isProfileClarified) ? (
          children
        ) : (
          <Loader open />
        )}
      </ApiContext.Provider>
    );
  }
);

export enum LoginType {
  OnlyLoggedIn = 'OnlyLoggedIn',
  NotLogginedAllowed = 'NotLogginedAllowed',
}

export const useApiContext = <
  T extends LoginType = LoginType.NotLogginedAllowed
>(): [T] extends [LoginType.OnlyLoggedIn]
  ? StrictApiContextValue
  : BaseApiContextValue =>
  useContext(ApiContext) as [T] extends [LoginType.OnlyLoggedIn]
    ? StrictApiContextValue
    : BaseApiContextValue;
